import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
  created_at: number;
}

export default function OrganizationList() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/organizations');
      setOrganizations(response.data.organizations || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async () => {
    if (!newOrgName.trim()) return;
    
    try {
      setCreating(true);
      const response = await axios.post('/api/organizations', {
        name: newOrgName,
        settings: {}
      });
      
      setOrganizations([...organizations, response.data]);
      setNewOrgName('');
      setError(null);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  if (loading && organizations.length === 0) {
    return <div className="p-4">Loading organizations...</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Your Organizations</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="New organization name"
            className="flex-1 px-3 py-2 border rounded"
          />
          <button
            onClick={createOrganization}
            disabled={creating || !newOrgName.trim()}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-blue-300"
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
      
      {organizations.length === 0 ? (
        <div className="text-gray-500">No organizations found. Create one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.map(org => (
            <Link
              key={org.id}
              to={`/organizations/${org.id}`}
              className="block p-4 border rounded hover:bg-gray-50"
            >
              <h2 className="text-xl font-semibold">{org.name}</h2>
              <div className="text-sm text-gray-500">Role: {org.role}</div>
              <div className="text-sm text-gray-500">
                Created: {new Date(org.created_at).toLocaleDateString()}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
